import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Validators } from '@helpers/constant';
import { useCurrentUser } from '@shared/hooks';
import { useForm } from 'react-hook-form';
import { TFunction } from 'i18next';
import { z } from 'zod';

const topUpFormSchema = (t: TFunction, currency: string) =>
  z.object({
    // Preprocess the amount to replace commas with dots, zod treats numbers with commas as strings
    amount: z.preprocess(
      (value) => (typeof value === 'string' ? value.replace(',', '.') : value),
      z
        .number({
          coerce: true,
          message: t('FORMS.SHOULD_BE_NUMBER'),
        })
        .min(20, t('FORMS.MIN_AMOUNT', { limit: 20, currency }))
        .refine((value) => Number(value.toFixed(2)) === value, {
          message: t('FORMS.INVALID_CURRENCY_VALUE'),
        }),
    ),
    firstName: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .min(3, { message: t('FORMS.MIN_CHARACTERS', { limit: 3 }) }),
    lastName: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .min(3, { message: t('FORMS.MIN_CHARACTERS', { limit: 3 }) }),
    country: z.string({ required_error: t('FORMS.REQUIRED') }),
    zipCode: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .regex(Validators.ZIP_CODE, { message: t('FORMS.INVALID_ZIP_CODE') }),
  });

export type TopUpFormSchema = z.infer<ReturnType<typeof topUpFormSchema>>;

export const useTopUpForm = () => {
  const { currency } = useCurrentUser();
  const { t } = useTranslation();

  const form = useForm<TopUpFormSchema>({
    resolver: zodResolver(topUpFormSchema(t, currency)),
    mode: 'onBlur',
    defaultValues: {
      amount: 0,
      firstName: '',
      lastName: '',
      country: undefined, // Select doesn't show required error when empty string is set
      zipCode: '',
    },
  });

  return { form };
};
